import type { CellContext } from '@tanstack/react-table';
import * as React from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import { compose } from 'redux';

import type { W_OrganizationDocument_FindAllDocumentsTable_VM_Row } from 'ab-api/web/organizationDocument/findDocumentsTable';

import { prettyBytes } from '@acceligentllc/shared/utils/formatting';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import TableButtonType from 'ab-enums/tableButtonType.enum';
import TableNameEnum from 'ab-enums/tableName.enum';

import type { TableQuery } from 'ab-common/dataStructures/tableQuery';

import { loadingToast, successToast } from '@acceligentllc/storybook';

import ConfirmationModal from 'af-components/ConfirmationModal';
import Table from 'af-components/Table';
import type { TableRef } from 'af-components/Table';
import ActionsCell from 'af-components/Table/Cells/ActionsCell';
import EmptyCell from 'af-components/Table/Cells/EmptyCell';
import type { TableProps } from 'af-components/Table/types';
import DateCell from 'af-components/Table6/Cells/DateCell';

import * as AttachmentActions from 'af-actions/attachment';
import * as OrganizationDocumentActions from 'af-actions/organizationDocument';

import DocumentPreviewModal from './modals/DocumentPreviewModal';
import FileUploadModal from './modals/FileUploadModal';
import type FileUploadFM from './modals/FileUploadModal/formModel';
import styles from './styles.module.scss';

const DELETE_DOCUMENT_BODY = <>This action cannot be undone. </>;
const DELETE_DOCUMENT_TEXT = 'Delete Document';

type Props = ConnectedProps<typeof connector>;

const OrganizationDocuments: React.FC<Props> = (props) => {
	const { findOrganizationDocumentsTable, deleteOrganizationDocument, createOrganizationDocument, downloadAttachment } = props;

	const [showFileUploadModal, setShowFileUploadModal] = React.useState(false);
	const [documentToDelete, setDocumentToDelete] = React.useState<Nullable<W_OrganizationDocument_FindAllDocumentsTable_VM_Row>>(null);
	const [previewAttachmentId, setPreviewAttachmentId] = React.useState<Nullable<number>>(null);

	const tableRef = React.useRef<TableRef<W_OrganizationDocument_FindAllDocumentsTable_VM_Row>>(null);

	const fetchData = React.useCallback(async (tableRequestModel: TableQuery) => {
		return await findOrganizationDocumentsTable(tableRequestModel);
	}, [findOrganizationDocumentsTable]);

	const handleRefreshTable = () => {
		if (tableRef.current) {
			tableRef.current.refreshTable();
		}
	};

	const onPreview = React.useCallback(async (row: W_OrganizationDocument_FindAllDocumentsTable_VM_Row) => {
		setPreviewAttachmentId(row.attachmentId);
	}, []);

	const onDownload = React.useCallback(async (row: W_OrganizationDocument_FindAllDocumentsTable_VM_Row) => {

		const dotIndex = row.name.lastIndexOf('.');
		const attachmentNameWithoutType = (dotIndex !== -1) ? row.name.substring(0, dotIndex) : row.name;

		const fileName = `${attachmentNameWithoutType}${row.type}`;

		loadingToast(
			fileName,
			{
				title: 'Downloading attachment...',
				description: fileName,
				timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG),
			}
		);

		await downloadAttachment(row.attachmentId, fileName);

		successToast(
			fileName,
			{
				title: 'Download complete',
				description: fileName,
				timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG),
			}
		);
	}, [downloadAttachment]);

	const onSubmit = React.useCallback(async (form: FileUploadFM) => {
		await createOrganizationDocument({ attachmentIds: form.uploadedAttachmentIds });
		handleRefreshTable();
	}, [createOrganizationDocument]);

	const openFileUploadModal = React.useCallback(async () => setShowFileUploadModal(true), []);
	const closeFileUploadModal = React.useCallback(() => setShowFileUploadModal(false), []);

	const openDeleteModal = React.useCallback((original: W_OrganizationDocument_FindAllDocumentsTable_VM_Row) => {
		setDocumentToDelete(original);
	}, []);

	const closeDeleteModal = React.useCallback(() => {
		setDocumentToDelete(null);
	}, []);

	const closePreviewModal = React.useCallback(() => {
		setPreviewAttachmentId(null);
	}, []);

	const confirmDeletion = React.useCallback(async () => {
		if (documentToDelete) {
			await deleteOrganizationDocument(documentToDelete.id);
			handleRefreshTable();
		}
	}, [documentToDelete, deleteOrganizationDocument]);

	const resolveActionsButton = React.useCallback((_cell: CellContext<W_OrganizationDocument_FindAllDocumentsTable_VM_Row, unknown>) => {
		const actions = [
			{
				onClick: () => onPreview(_cell.row.original),
				label: 'Preview',
				shouldRefresh: false,
			},
			{
				onClick: () => onDownload(_cell.row.original),
				label: 'Download',
				shouldRefresh: false,
			},
			{
				onClick: () => openDeleteModal(_cell.row.original),
				label: 'Delete',
				shouldRefresh: true,
			},
		];

		return (
			<ActionsCell
				id="actions"
				isActionDropdown={true}
				labelKey="label"
				options={actions}
				valueKey="label"
			/>
		);
	}, [onDownload, onPreview, openDeleteModal]);

	const columns: TableProps<W_OrganizationDocument_FindAllDocumentsTable_VM_Row>['columns'] = React.useMemo(() => [
		{
			id: 'name',
			header: 'File Name',
			accessor: 'name',
			enableSorting: true,
			cell: ({ cell }) => (
				<>
					<div className={`icon-zoom_in ${styles.cellIcon}`} onClick={onPreview.bind(this, cell.row.original)} />
					<div className={`text-strong ${styles.attachmentCell}`}>
						{cell.row.original.name}
					</div>
				</>
			),
		},
		{
			id: 'size',
			header: 'Size',
			accessor: 'size',
			enableSorting: true,
			cell: ({ cell }) => <div className={styles.attachmentCell}>{prettyBytes(cell.row.original.size)}</div>,
		},
		{
			id: 'uploaded',
			header: 'Uploaded',
			accessor: 'name',
			enableSorting: true,
			cell: ({ cell }) => <DateCell date={cell.row.original.uploadedAt} format={TimeFormat.FULL_DATE} isLeftAligned={true} withTime={true} />,
		},
		{
			id: 'actions',
			isDisplayColumn: true,
			header: () => <EmptyCell isHeader />,
			cell: resolveActionsButton,
		},
	], [onPreview, resolveActionsButton]);

	const buttons = React.useMemo(() => {
		return [
			{
				type: TableButtonType.LINK,
				hasPermission: true,
				label: 'New Document Upload',
				icon: 'ActionImport',
				onClick: openFileUploadModal,
			},
		];
	}, [openFileUploadModal]);

	const deleteDocumentModalTitle = React.useMemo(() => (`Are you sure you want to delete this Document for organization (${documentToDelete?.name})?`), [documentToDelete]);

	return (
		<>
			<Table
				buttons={buttons}
				columns={columns}
				fetch={fetchData}
				hasSearchInput={true}
				hasSubRows={false}
				offsetHeight={25}
				ref={tableRef}
				searchLabel="JHA Documents"
				tableName={TableNameEnum.ORGANIZATION_DOCUMENTS}
			/>
			<FileUploadModal
				closeModal={closeFileUploadModal}
				onSubmit={onSubmit}
				showModal={showFileUploadModal}
			/>
			<ConfirmationModal
				body={DELETE_DOCUMENT_BODY}
				closeModal={closeDeleteModal}
				confirmAction={confirmDeletion}
				confirmText={DELETE_DOCUMENT_TEXT}
				modalStyle="danger"
				showModal={!!documentToDelete}
				title={deleteDocumentModalTitle}
			/>
			<DocumentPreviewModal
				attachmentId={previewAttachmentId}
				closeModal={closePreviewModal}
				showModal={!!previewAttachmentId}
			/>
		</>
	);
};

function mapDispatchToProps() {
	return {
		findOrganizationDocumentsTable: OrganizationDocumentActions.findOrganizationDocumentsTable,
		deleteOrganizationDocument: OrganizationDocumentActions.deleteOrganizationDocument,
		createOrganizationDocument: OrganizationDocumentActions.createOrganizationDocument,
		downloadAttachment: AttachmentActions.downloadAttachment,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<Props>>(
	React.memo,
	connector
);

export default enhance(OrganizationDocuments);
